import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { FormFooter } from "@/components/molecules/form-footer";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageSkeleton } from "@/components/molecules/page-skeleton";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AgentName } from "@/components/molecules/agent-name";
import { useAgents } from "@/features/agents/hooks/use-agents";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useAvailableTools } from "@/features/tools/hooks/use-tools";
import { useToolCatalog } from "@/features/tools/hooks/use-tool-catalog";
import { useTranslation } from "@/features/utils/i18n";
import { WrenchIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { hasAgentOverride } from "@/views/backoffice/agents/components/agent-skills";
import { PageLayout } from "../layout";
import { DynamicForm } from "./components/dynamic-form";
import { ToolActivationSwitch } from "./components/tool-activation-switch";
import { useToolSettings } from "./hooks/use-tool-settings";

export function ConfigureToolPage() {
  const { t } = useTranslation("tools");
  const catalog = useToolCatalog();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const company = useCurrentClient()?.client?.id;
  const {
    data: available = [],
    isLoading,
    isError,
    refetch,
  } = useAvailableTools();

  const { data: agents = [] } = useAgents();

  const availableTool = available.find((item) => item.tool.id === id);
  const {
    tool,
    isActive,
    formData,
    formRef,
    handleChange,
    dirty,
    saving,
    save,
    reset,
    customFields,
    fieldVisibility,
    fieldGroups,
  } = useToolSettings({ availableTool });
  // The agents this form does not fully speak for. Shown whatever the number of
  // agents: a sole agent can still hold an override, and it wins on its calls.
  // An empty entry, or one left by a deleted agent, overrides nothing.
  const agentsWithOverrides = useMemo(() => {
    if (!availableTool?.clientTool?.per_agents) return [];
    return Object.keys(availableTool.clientTool.per_agents).filter(
      (agentId) =>
        agents.some((agent) => agent.id === agentId) &&
        hasAgentOverride(availableTool, agentId),
    );
  }, [availableTool, agents]);

  const backTo = {
    label: t("tools.sidebar.allTools"),
    path: `/${company}/settings/tools`,
  };

  if (isLoading) return <PageSkeleton width="medium" blocks={["h-64"]} />;

  if (isError || !tool || !availableTool) {
    return (
      <PageLayout width="medium" title={t("tools.title")} backTo={backTo}>
        {isError ? (
          <ErrorState onRetry={refetch} />
        ) : (
          <EmptyState
            icon={WrenchIcon}
            title={t("tools.configure.notFoundMessage")}
          />
        )}
      </PageLayout>
    );
  }

  return (
    <PageLayout
      width="medium"
      title={catalog.label(tool)}
      description={catalog.description(tool)}
      backTo={backTo}
      actions={
        <ToolActivationSwitch
          availableTool={availableTool}
          showLabel
          // Edits to a module being switched off have nowhere to go.
          onBeforeDeactivate={reset}
        />
      }
    >
      {/* Agent overrides indicator */}
      {agentsWithOverrides.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {t("tools.configure.overriddenFor")}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Badge
                variant="secondary"
                className="cursor-pointer hover:bg-secondary/80"
              >
                {t("tools.configure.agents", {
                  count: agentsWithOverrides.length,
                })}
              </Badge>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {agentsWithOverrides.map((agentId) => (
                <DropdownMenuItem
                  key={agentId}
                  onClick={() =>
                    navigate(`/${company}/agents/${agentId}?tool=${id}`)
                  }
                >
                  <AgentName agentId={agentId} showAvatar={true} />
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {!isActive && (
        <p className="text-sm text-muted-foreground">
          {t("tools.configure.activateToEdit")}
        </p>
      )}

      {/* Every edit waits for the save bar, as in the company settings. */}
      <fieldset
        ref={formRef}
        disabled={!isActive}
        className="min-w-0 disabled:opacity-60"
      >
        {fieldGroups ? (
          <DynamicForm
            schema={catalog.schema(tool)}
            formData={formData}
            onChange={handleChange}
            customFields={customFields}
            fieldVisibility={fieldVisibility}
            fieldGroups={fieldGroups}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>{t("tools.configure.configurationTitle")}</CardTitle>
              <CardDescription>
                {t("tools.configure.configurationDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <DynamicForm
                schema={catalog.schema(tool)}
                formData={formData}
                onChange={handleChange}
                customFields={customFields}
                fieldVisibility={fieldVisibility}
              />
            </CardContent>
          </Card>
        )}
      </fieldset>

      <FormFooter
        dirty={dirty}
        saving={saving}
        onSave={save}
        onCancel={reset}
      />
    </PageLayout>
  );
}
